import { useEffect, useState, useCallback, useMemo } from "react";
import { useTeams } from "../lib/teams";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Plus, Wallet, AlertCircle, CheckCircle2, Circle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import PayNowButton from "../components/payments/PayNowButton";

const DEFAULT_INSTALLMENTS = 10; // 10 monthly installments

function buildInstallments(total, count, startMonth = 9) {
  // start month default September (FIGC season)
  const amount = Math.round((total / count) * 100) / 100;
  const today = new Date();
  const year = today.getMonth() + 1 >= 7 ? today.getFullYear() : today.getFullYear() - 1;
  return Array.from({ length: count }, (_, i) => {
    const m = (startMonth - 1 + i) % 12;
    const y = year + Math.floor((startMonth - 1 + i) / 12);
    const due = new Date(y, m, 10);
    return {
      due_date: due.toISOString().slice(0, 10),
      amount,
    };
  });
}

function isOverdue(installment) {
  if (installment.paid_at) return false;
  return installment.due_date < new Date().toISOString().slice(0, 10);
}

export default function AdminPage() {
  const { activeTeam, canEdit } = useTeams();
  const [players, setPlayers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedPlayerId, setSelectedPlayerId] = useState("");
  const [total, setTotal] = useState(300);
  const [installmentsCount, setInstallmentsCount] = useState(DEFAULT_INSTALLMENTS);
  const [season, setSeason] = useState("");
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    if (!activeTeam) return;
    const [p, pl] = await Promise.all([
      api.get(`/teams/${activeTeam.id}/players`),
      api.get(`/teams/${activeTeam.id}/payments`),
    ]);
    setPlayers(p.data);
    setPlans(pl.data);
    setSeason(activeTeam.season || "2025/26");
  }, [activeTeam]);

  useEffect(() => {
    load();
  }, [load]);

  const planByPlayer = useMemo(() => {
    const map = {};
    plans.forEach((pl) => (map[pl.player_id] = pl));
    return map;
  }, [plans]);

  if (!activeTeam) return <p className="text-slate-500">Seleziona una squadra.</p>;

  const submit = async () => {
    if (!selectedPlayerId) return toast.error("Seleziona giocatore");
    const installments = buildInstallments(Number(total), Number(installmentsCount));
    await api.post("/payment-plans", {
      team_id: activeTeam.id,
      player_id: selectedPlayerId,
      season,
      total_amount: Number(total),
      notes,
      installments,
    });
    toast.success("Piano pagamenti salvato");
    setOpen(false);
    load();
  };

  const togglePaid = async (plan, idx) => {
    const inst = plan.installments[idx];
    if (inst.paid_at) {
      await api.post(`/payment-plans/${plan.id}/unpay`, { index: idx });
    } else {
      await api.post(`/payment-plans/${plan.id}/pay`, { index: idx, paid_amount: inst.amount });
    }
    load();
  };

  const overdueCount = (plan) => (plan.installments || []).filter(isOverdue).length;
  const paidCount = (plan) => (plan.installments || []).filter((i) => i.paid_at).length;

  const overallStats = useMemo(() => {
    let totalDue = 0;
    let totalPaid = 0;
    let overdueTot = 0;
    plans.forEach((pl) => {
      (pl.installments || []).forEach((i) => {
        totalDue += i.amount || 0;
        if (i.paid_at) totalPaid += i.paid_amount || i.amount || 0;
        else if (isOverdue(i)) overdueTot += i.amount || 0;
      });
    });
    return { totalDue, totalPaid, overdueTot };
  }, [plans]);

  const playersWithoutPlan = players.filter((p) => !planByPlayer[p.id]);

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Amministrazione</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Quote & pagamenti</h1>
          <p className="text-slate-500">
            {plans.length} piani attivi · {players.length - plans.length} giocatori senza piano
          </p>
        </div>
        {canEdit && (
          <Button
            onClick={() => {
              setSelectedPlayerId(playersWithoutPlan[0]?.id || "");
              setTotal(300);
              setInstallmentsCount(DEFAULT_INSTALLMENTS);
              setNotes("");
              setOpen(true);
            }}
            className="rounded-full bg-sky-500 hover:bg-sky-600"
            data-testid="new-plan-btn"
          >
            <Plus size={16} className="mr-1" /> Piano pagamenti
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5 border-slate-200">
          <div className="label-eyebrow">Totale incassato</div>
          <div className="font-heading font-bold text-3xl mt-1 text-emerald-600 tabular-nums">
            € {overallStats.totalPaid.toFixed(0)}
          </div>
          <div className="text-xs text-slate-400 mt-1">su € {overallStats.totalDue.toFixed(0)} previsti</div>
        </Card>
        <Card className="p-5 border-slate-200">
          <div className="label-eyebrow">In sollecito</div>
          <div className="font-heading font-bold text-3xl mt-1 text-red-600 tabular-nums">
            € {overallStats.overdueTot.toFixed(0)}
          </div>
          <div className="text-xs text-slate-400 mt-1">rate scadute non pagate</div>
        </Card>
        <Card className="p-5 border-slate-200">
          <div className="label-eyebrow">Da incassare</div>
          <div className="font-heading font-bold text-3xl mt-1 tabular-nums">
            € {(overallStats.totalDue - overallStats.totalPaid).toFixed(0)}
          </div>
          <div className="text-xs text-slate-400 mt-1">residuo della stagione</div>
        </Card>
      </div>

      <div className="space-y-3">
        {players.map((p) => {
          const plan = planByPlayer[p.id];
          const overdue = plan ? overdueCount(plan) : 0;
          const paid = plan ? paidCount(plan) : 0;
          const totalInst = plan ? (plan.installments || []).length : 0;
          return (
            <Card key={p.id} className="p-4 border-slate-200" data-testid={`payment-row-${p.id}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="h-7 min-w-7 px-1 rounded bg-sky-100 text-sky-700 text-xs font-bold flex items-center justify-center">
                  {p.jersey_number ?? "—"}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">
                    {p.last_name} {p.first_name}
                  </div>
                  {plan ? (
                    <div className="text-xs text-slate-500 mt-0.5">
                      Quota €{plan.total_amount} · {paid}/{totalInst} rate pagate
                    </div>
                  ) : (
                    <div className="text-xs text-slate-400 mt-0.5">Nessun piano attivo</div>
                  )}
                </div>
                {overdue > 0 && (
                  <span className="px-2 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700 inline-flex items-center gap-1">
                    <AlertCircle size={12} /> {overdue} in sollecito
                  </span>
                )}
                {plan && paid === totalInst && (
                  <span className="px-2 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700 inline-flex items-center gap-1">
                    <CheckCircle2 size={12} /> Saldato
                  </span>
                )}
              </div>
              {plan && (
                <div className="mt-3 flex gap-1.5 flex-wrap">
                  {(plan.installments || []).map((inst, idx) => {
                    const overdueThis = isOverdue(inst);
                    return (
                      <div key={`${plan.id}-${inst.due_date || idx}`} className="inline-flex items-stretch rounded-lg overflow-hidden border border-slate-200">
                        <button
                          onClick={() => canEdit && togglePaid(plan, idx)}
                          disabled={!canEdit}
                          className={`px-2 py-1 text-xs font-medium inline-flex items-center gap-1 transition ${
                            inst.paid_at
                              ? "bg-emerald-50 text-emerald-700"
                              : overdueThis
                              ? "bg-red-50 text-red-700"
                              : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                          }`}
                          title={`Scadenza ${inst.due_date}`}
                          data-testid={`installment-${plan.id}-${idx}`}
                        >
                          {inst.paid_at ? (
                            <CheckCircle2 size={11} />
                          ) : overdueThis ? (
                            <AlertCircle size={11} />
                          ) : (
                            <Circle size={11} />
                          )}
                          {inst.due_date.slice(5)} · €{inst.amount}
                        </button>
                        {!inst.paid_at && canEdit && (
                          <PayNowButton
                            kind="installment"
                            itemId={plan.id}
                            subId={String(idx)}
                            label=""
                            size="sm"
                            variant="ghost"
                            className="px-1.5 h-auto border-l border-slate-200 rounded-none text-sky-600 hover:bg-sky-50"
                            testId={`pay-installment-${plan.id}-${idx}`}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          );
        })}
        {players.length === 0 && (
          <Card className="p-10 text-center text-slate-500 border-dashed">
            Aggiungi giocatori nell'anagrafica per gestire i pagamenti.
          </Card>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Piano pagamenti</DialogTitle>
          <DialogDescription className="sr-only">Imposta il piano di pagamento per un giocatore.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Giocatore</Label>
              <Select value={selectedPlayerId} onValueChange={setSelectedPlayerId}>
                <SelectTrigger data-testid="plan-player-select">
                  <SelectValue placeholder="Seleziona..." />
                </SelectTrigger>
                <SelectContent>
                  {players.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.last_name} {p.first_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Quota totale (€)</Label>
                <Input
                  type="number"
                  value={total}
                  onChange={(e) => setTotal(e.target.value)}
                  data-testid="plan-total-input"
                />
              </div>
              <div>
                <Label>Numero rate</Label>
                <Input
                  type="number"
                  value={installmentsCount}
                  onChange={(e) => setInstallmentsCount(e.target.value)}
                />
              </div>
            </div>
            <div>
              <Label>Stagione</Label>
              <Input value={season} onChange={(e) => setSeason(e.target.value)} placeholder="2025/26" />
            </div>
            <div>
              <Label>Note</Label>
              <Textarea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Es. iscrizione + materiale tecnico"
              />
            </div>
            <p className="text-xs text-slate-500">
              Le rate vengono distribuite mensilmente partendo da settembre. Puoi marcare ogni rata come pagata cliccandoci sopra.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Annulla
            </Button>
            <Button
              onClick={submit}
              className="bg-sky-500 hover:bg-sky-600"
              data-testid="save-plan-btn"
            >
              <Wallet size={14} className="mr-1" /> Salva piano
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
